import { ColorTypes, CustomProperties, utils, actions, MenuItem, dates } from "@omegagrid/core";
import { CellModel, ColumnSource, MenuOptions, OptionSource, RequestFilters, RowSource, TableTheme } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import { ToolbarItemOptions } from "../types";
import { GridEditor } from "./gridEditor";
import { createWorkbook, exportXLSX } from "@omegagrid/plugin-xlsx";
import { BaseGridAdapter } from "../adapters/baseGridAdapter";
import { createExportFilename } from '../helpers';

export type SaveMode = 'edited_rows'|'edited_cells'|'all_cells'|'all_rows'|'custom';
export type ExportToolbarItemOptions = ToolbarItemOptions & {items?: MenuItem[]};

export type ExportType = 'xlsx'|'csv';

export type GridEditorOptions = OptionSource & {
	saveMode?: SaveMode,
	refreshAfterSave?: boolean,
	toolbar?: {
		refresh?: ToolbarItemOptions,
		autoUpdate?: ToolbarItemOptions,
		save?: ToolbarItemOptions,
		discard?: ToolbarItemOptions,
		new?: ToolbarItemOptions,
		rowPasteMode?: ToolbarItemOptions,
		export?: ExportToolbarItemOptions,
	},
	customToolbar?: ToolbarItem[],
	rowMenu?: {
		addRows?: boolean,
		deleteRows?: boolean,
	},
	customMenus?: MenuOptions,
	showEmptyRow?: boolean,
	exportTypes?: ExportType[],
	lightTheme?: TableTheme,
	darkTheme?: TableTheme,
	autoUpdateInterval?: number,
	autoUpdate?: boolean,
	keyboardShortcuts?: boolean,
}

export type CellState = 'new'|'edited'|'deleted';
export type RowData = {data?: Record<string, unknown>, state?: CellState, index?: number, props?: CustomProperties}
export type CellData = {r: number, c: number, v: unknown, state?: CellState, props?: CustomProperties}

const STATE_COLORS = {
	deleted:    { bg: '#a13f3f', text: '#d3d3d3' },
	new:        { bg: '#00497e', text: '#d3d3d3' },
	editedCell: { bg: '#636318', text: '#d3d3d3' },
	editedRow:  { bg: '#4e4e13', text: '#d3d3d3' },
} as const;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_AUTO_UPDATE_INTERVAL = 10000;
const DEFAULT_DATE_FORMAT = dates.getDateFormat({ withTime: true });

export const DEFAULT_GRID_EDITOR_OPTIONS: GridEditorOptions = {
	saveMode: 'edited_rows',
	pageSize: DEFAULT_PAGE_SIZE,
	refreshAfterSave: false,
	emptyPlaceholder: true,
	formulas: false,
	autoUpdateInterval: DEFAULT_AUTO_UPDATE_INTERVAL,
	dateFormat: DEFAULT_DATE_FORMAT,
	hideOuterBorder: true,
	clipboard: {
		pasteProps: 'all',
		rowPasteMode: 'rewrite_append'
	},
	keyboardShortcuts: true,
	toolbar: {
		refresh: {
			text: null,
			color: ColorTypes.Gray,
			icon: 'arrows-rotate',
			enabled: true
		},
		autoUpdate: {
			text: 'Auto update',
			color: ColorTypes.Accent,
			icon: 'arrows-spin',
			enabled: false,
		},
		save: {
			text: 'Save changes',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true
		},
		discard: {
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true
		},
		new: {
			text: 'New',
			color: ColorTypes.Blue,
			icon: 'plus',
			enabled: true
		},
		rowPasteMode: {
			enabled: false
		},
		export: {
			text: 'Export',
			color: ColorTypes.Gray,
			icon: 'download',
			items: [
				{key: 'export.xlsx', value: 'XLSX', icon: 'file-excel'},
				{key: 'export.csv', value: 'CSV', icon: 'file-csv'},
			],
			enabled: false
		}
	},
	afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
		const c =
			cell.row.getProperty('deleted') ? STATE_COLORS.deleted :
			cell.row.getProperty('new')     ? STATE_COLORS.new :
			cell.getProperty('edited')      ? STATE_COLORS.editedCell :
			cell.row.getProperty('edited')  ? STATE_COLORS.editedRow :
			null;

		if (c) {
			div.style.backgroundColor = c.bg;
			div.style.color = c.text;
		}
		return div;
	},
	lightTheme: {
		name: 'light',
		cells: [{
			conditions: [{property: 'editable', value: false}],
			style: {fgColor: 'f3f3f3'}
		}]
	},
	darkTheme: {
		name: 'dark',
		cells: [{
			conditions: [{property: 'editable', value: false}],
			style: {fgColor: '1a1a1a', color: '9b9b9b'}
		}]
	},
}

export type GridPagingState = {
	options?: { pageSize?: number | null } | null,
	rowRoot: { isLoaded: boolean },
};

/**
 * True when the model holds every row the adapter can return: the grid is not paged
 * (`pageSize` null or 0), or its last page has already arrived.
 */
export function allRowsLoaded(model: GridPagingState | null | undefined): boolean {
	if (!model) return false;
	return !((model.options?.pageSize ?? 0) > 0) || model.rowRoot.isLoaded;
}

export abstract class GridEditorAdapter extends BaseGridAdapter {
	protected pageSize = DEFAULT_PAGE_SIZE;
	public editor: GridEditor;
	afterLoad(): void { }
	dispose(): void { }
	abstract getColumns(): null|ColumnSource[]|Promise<ColumnSource[]>;
	abstract getRows(filters?: RequestFilters): null|RowSource[]|Promise<RowSource[]>;
	async save(_data?: (RowData|CellData)[]): Promise<void|RowSource> { }
	getOptions(): null|Partial<GridEditorOptions>|Promise<Partial<GridEditorOptions>> {
		return Object.assign({}, DEFAULT_GRID_EDITOR_OPTIONS);
	}
	createOptions(options: Partial<GridEditorOptions>): GridEditorOptions {
		options = utils.mergeDeep({}, DEFAULT_GRID_EDITOR_OPTIONS, options);
		this.pageSize = options.pageSize;
		return options;
	}
	onAction(action: string, cell: CellModel, event: actions.ActionEvent = null) { }

	async export(type: ExportType) {
		if (!this.component.model.rowRoot.isLoaded) {
			const pageSize = this.pageSize;
			this.pageSize = 1000000; // Set a large page size to load all data
			this.component.model.rowRoot.clearChildren();
			await this.component.rendering.load();
			this.pageSize = pageSize; // Restore original page size
		}

		switch (type) {
			case 'xlsx':
				await exportXLSX(await createWorkbook([this.component.model], {
					defaultBorders: true,
					dropdownCells: "displayValue",
					exportHiddenColumns: false,
					exportHiddenRows: false,
					gridLines: false,
				}), createExportFilename(type));
				break;
			default:
				console.error(`Export type "${type}" is not supported.`);
		}
	}
}
